import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "../api";
import { Alert, Badge, Button, Card, DataTable, Empty, ErrorBox, FormError, Loading, Modal, Money, PageHeader, Reg, Tabs, TextInput, useDebounced, useToast } from "../ui";
import { CustomerFormModal, DeleteRecordButton } from "../components";
import { DeptBadge, InvoiceStatus, JobStatusBadge, QuoteStatusBadge, date } from "../format";
import { CONTACT_PREF_LABEL, PAYMENT_METHOD_LABEL } from "../../shared/labels";
import { useIsOwner } from "../auth";
import { RefundModal } from "./Invoices";

export function CustomerList() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const dq = useDebounced(q.trim());
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ["customers", dq], queryFn: () => api.get(`/customers${qs({ q: dq })}`) });
  const owner = useIsOwner();
  const dups = useQuery({ queryKey: ["customer-duplicates"], queryFn: () => api.get("/customers/duplicates/review"), enabled: owner });
  const [reviewing, setReviewing] = useState(false);
  const pairs = dups.data?.pairs ?? [];
  return (
    <>
      <PageHeader title="Customers" actions={<>
        {owner && pairs.length > 0 && <Button onClick={() => setReviewing(true)}>Possible duplicates ({pairs.length})</Button>}
        <Button variant="primary" onClick={() => setCreating(true)}>+ New customer</Button>
      </>} />
      <div className="filters">
        <input className="input" style={{ maxWidth: 420 }} placeholder="Search name, phone, email, postcode or reg" value={q}
          onChange={(e) => { setQ(e.target.value); setParams(e.target.value ? { q: e.target.value } : {}, { replace: true }); }} aria-label="Search customers" />
      </div>
      <Card pad={false}>
        {list.isLoading ? <Loading /> : list.isError ? <ErrorBox error={list.error} retry={() => list.refetch()} /> : (
          <DataTable rows={list.data.customers} rowLink={(c: any) => `/customers/${c.id}`}
            empty={<Empty title={dq ? "No customers match" : "No customers yet"} action={<Button variant="primary" onClick={() => setCreating(true)}>Add a customer</Button>} />}
            columns={[
              { key: "n", label: "Name", render: (c: any) => <><strong>{c.name}</strong>{c.merged_count > 0 && <> <Badge>+{c.merged_count} merged</Badge></>}</> },
              { key: "p", label: "Phone", render: (c: any) => c.phone ?? "—" },
              { key: "v", label: "Vehicles", render: (c: any) => c.vehicles ? c.vehicles.split(", ").map((r: string) => <Reg key={r} reg={r} />) : <span className="muted">—</span> },
              { key: "b", label: "Balance owed", num: true, render: (c: any) => c.balance_pence > 0 ? <Money pence={c.balance_pence} className="badge red" /> : <span className="muted">—</span> },
              { key: "l", label: "Last job", render: (c: any) => c.last_job_at ? date(c.last_job_at.slice(0, 10)) : "—" },
            ]} />
        )}
      </Card>
      {creating && <CustomerFormModal onClose={() => setCreating(false)} onSaved={(id) => navigate(`/customers/${id}`)} />}
      {reviewing && <DuplicateReview pairs={pairs} onClose={() => setReviewing(false)} />}
    </>
  );
}

/** Pairs that look like the same person. Nothing is merged unless the owner chooses to. */
function DuplicateReview({ pairs, onClose }: { pairs: any[]; onClose: () => void }) {
  const [merging, setMerging] = useState<any>(null);
  if (merging) return <MergeModal pair={merging} onClose={() => setMerging(null)} onDone={onClose} />;
  return (
    <Modal wide title={`Possible duplicate customers (${pairs.length})`} onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      <p className="small muted">These pairs share a phone number, email or name. They may be the same person entered twice — or two different people. Nothing is merged unless you choose to.</p>
      <DataTable rows={pairs} columns={[
        { key: "a", label: "Customer", render: (p: any) => <><strong>{p.a.name}</strong><div className="small muted">{p.a.phone ?? "no phone"} · {p.a.invoices} invoices · {p.a.jobs} jobs</div></> },
        { key: "b", label: "Possible duplicate", render: (p: any) => <><strong>{p.b.name}</strong><div className="small muted">{p.b.phone ?? "no phone"} · {p.b.invoices} invoices · {p.b.jobs} jobs</div></> },
        { key: "r", label: "Why", render: (p: any) => <>{p.reasons.map((r: string) => <Badge key={r} tone={p.strength === "strong" ? "amber" : undefined}>{r}</Badge>)}</> },
        { key: "x", label: "", render: (p: any) => <Button size="sm" onClick={() => setMerging(p)}>Review…</Button> },
      ]} />
    </Modal>
  );
}

/** Owner merges one record into the other. A link only: invoices, payments and history stay exactly as they are. */
function MergeModal({ pair, onClose, onDone }: { pair: any; onClose: () => void; onDone: () => void }) {
  const [keep, setKeep] = useState<"a" | "b">(pair.a.invoices + pair.a.jobs >= pair.b.invoices + pair.b.jobs ? "a" : "b");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const target = keep === "a" ? pair.a : pair.b, source = keep === "a" ? pair.b : pair.a;
  const preview = useQuery({ queryKey: ["merge-preview", source.id, target.id], queryFn: () => api.get(`/customers/${source.id}/merge-preview?into=${target.id}`) });
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const m = useMutation({
    mutationFn: () => api.post(`/customers/${source.id}/merge-into`, { targetId: target.id, reason, confirm }),
    onSuccess: () => { ["customers", "customer", "customer-duplicates"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast("success", `“${source.name}” merged into “${target.name}”`); onDone(); navigate(`/customers/${target.id}`); },
  });
  const blockers: string[] = preview.data?.blockers ?? [];
  const Side = ({ x, which }: { x: any; which: "a" | "b" }) => (
    <label className="card" style={{ padding: 12, display: "block", cursor: "pointer", borderColor: keep === which ? "var(--navy-800)" : undefined }}>
      <input type="radio" name="keep" checked={keep === which} onChange={() => setKeep(which)} /> <strong>Keep {x.name}</strong>
      <div className="small">{x.phone ?? "no phone"}{x.email ? ` · ${x.email}` : ""}{x.postcode ? ` · ${x.postcode}` : ""}</div>
      <div className="small muted">{x.vehicles} vehicles · {x.jobs} jobs · {x.invoices} invoices · {x.payments} payments · added {date(x.created_at.slice(0, 10))}</div>
    </label>
  );
  return (
    <Modal wide title="Merge duplicate customers" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" loading={m.isPending} disabled={blockers.length > 0 || reason.trim().length < 3 || confirm !== "MERGE"} onClick={() => m.mutate()}>Merge</Button></>}>
      <div className="grid grid-2"><Side x={pair.a} which="a" /><Side x={pair.b} which="b" /></div>
      <div style={{ marginTop: 12 }}><Alert kind="info" title="What merging does">
        “{source.name}” is linked to “{target.name}” and leaves the customer list. Its invoices, payments, credit notes, jobs and quotes are <b>not changed</b> — they keep their original customer and appear on “{target.name}”'s page. Its vehicles move to “{target.name}”. Nothing is deleted, and the merge can be undone from “{target.name}”'s page.
      </Alert></div>
      {blockers.length > 0 && <div style={{ marginTop: 12 }}><Alert kind="warn" title="This merge cannot go ahead yet">{blockers.map((b) => <div key={b}>{b}</div>)}</Alert></div>}
      <div className="form-grid" style={{ marginTop: 12 }}>
        <TextInput label="Why are they the same person?" full value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. same phone number, entered twice" />
        <TextInput label="Type MERGE to confirm" value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} />
      </div>
      <FormError error={m.error} />
    </Modal>
  );
}

export function CustomerDetail() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ["customer", id], queryFn: () => api.get(`/customers/${id}`) });
  const [tab, setTab] = useState<"jobs" | "quotes" | "invoices" | "payments" | "vehicles">("jobs");
  const [editing, setEditing] = useState(false);
  const [refund, setRefund] = useState(false);
  const owner = useIsOwner();
  const navigate = useNavigate();
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const { customer: c, vehicles, previousVehicles, jobs, quotes, invoices, payments, creditPence, mergedInto, mergedFrom } = q.data;
  const fromMerged = (row: any) => row.customer_id && row.customer_id !== c.id ? <> <Badge>from {mergedFrom.find((m: any) => m.id === row.customer_id)?.name ?? "merged record"}</Badge></> : null;
  const owed = invoices.filter((i: any) => i.status === "issued").reduce((a: number, i: any) => a + Math.max(i.outstanding_pence, 0), 0);
  return (
    <>
      <PageHeader title={c.name} back={{ to: "/customers", label: "Customers" }}
        sub={<>{c.phone ?? "No phone"}{c.email ? ` · ${c.email}` : ""} · prefers {CONTACT_PREF_LABEL[c.contact_pref as keyof typeof CONTACT_PREF_LABEL]}</>}
        actions={<>
          <Button onClick={() => setEditing(true)}>Edit</Button>
          <DeleteRecordButton kind="customer" id={c.id} onDone={() => navigate("/customers")} />
          <Link className="btn btn-secondary" to={`/quotes/new?customer=${c.id}`}>New quote</Link>
          <Link className="btn btn-primary" to={`/jobs/new?customer=${c.id}`}>New job</Link>
        </>} />
      {mergedInto && <div style={{ marginBottom: 12 }}><Alert kind="info" title="This record was merged">It is linked to <Link to={`/customers/${mergedInto.id}`}>{mergedInto.name}</Link>, where its history is shown together. Its invoices and payments are unchanged.</Alert></div>}
      <div className="grid grid-sidebar">
        <div>
          <Tabs value={tab} onChange={setTab} tabs={[
            { value: "jobs", label: `Jobs (${jobs.length})` }, { value: "quotes", label: `Quotes (${quotes.length})` }, { value: "invoices", label: `Invoices (${invoices.length})` },
            { value: "payments", label: `Payments (${payments.length})` }, { value: "vehicles", label: `Vehicles (${vehicles.length})` },
          ]} />
          <Card pad={false}>
            {tab === "jobs" && <DataTable rows={jobs} rowLink={(j: any) => `/jobs/${j.id}`} empty={<Empty title="No jobs yet" />} columns={[
              { key: "n", label: "Job", render: (j: any) => <><strong>{j.number}</strong>{fromMerged(j)}</> },
              { key: "r", label: "Vehicle", render: (j: any) => <Reg reg={j.registration_at_job} /> },
              { key: "d", label: "Dept", render: (j: any) => <DeptBadge d={j.department} /> },
              { key: "s", label: "Status", render: (j: any) => <JobStatusBadge s={j.status} /> },
              { key: "t", label: "Opened", render: (j: any) => date(j.created_at.slice(0, 10)) },
            ]} />}
            {tab === "quotes" && <DataTable rows={quotes} rowLink={(x: any) => `/quotes/${x.id}`} empty={<Empty title="No quotes" />} columns={[
              { key: "n", label: "Quote", render: (x: any) => <><strong>{x.number}</strong>{fromMerged(x)}</> },
              { key: "s", label: "Status", render: (x: any) => <QuoteStatusBadge s={x.status} /> },
              { key: "t", label: "Total", num: true, render: (x: any) => <Money pence={x.total_pence} /> },
              { key: "d", label: "Created", render: (x: any) => date(x.created_at.slice(0, 10)) },
            ]} />}
            {tab === "invoices" && <DataTable rows={invoices} rowLink={(x: any) => `/invoices/${x.id}`} empty={<Empty title="No invoices" />} columns={[
              { key: "n", label: "Invoice", render: (x: any) => <><strong>{x.number}</strong>{fromMerged(x)}</> },
              { key: "d", label: "Date", render: (x: any) => date(x.issue_date) },
              { key: "t", label: "Total", num: true, render: (x: any) => <Money pence={x.total_pence} /> },
              { key: "o", label: "Outstanding", num: true, render: (x: any) => <Money pence={x.outstanding_pence} /> },
              { key: "s", label: "Status", render: (x: any) => <InvoiceStatus status={x.status} total={x.total_pence} paid={x.paid_pence} credited={x.credited_pence} /> },
            ]} />}
            {tab === "payments" && <DataTable rows={payments} empty={<Empty title="No payments" />} columns={[
              { key: "d", label: "Date", render: (x: any) => <>{date(x.received_on)}{fromMerged(x)}</> },
              { key: "k", label: "Type", render: (x: any) => x.kind === "deposit" ? <Badge tone="blue">Deposit</Badge> : "Payment" },
              { key: "m", label: "Method", render: (x: any) => PAYMENT_METHOD_LABEL[x.method as keyof typeof PAYMENT_METHOD_LABEL] },
              { key: "a", label: "Amount", num: true, render: (x: any) => x.voided_at ? <s><Money pence={x.amount_pence} /></s> : <Money pence={x.amount_pence} /> },
              { key: "r", label: "Reference", render: (x: any) => (x.voided_at ? <Badge tone="red">Voided</Badge> : x.reference ?? "") },
            ]} />}
            {tab === "vehicles" && <>
              <DataTable rows={vehicles} rowLink={(v: any) => `/vehicles/${v.id}`} empty={<Empty title="No vehicles linked" />} columns={[
                { key: "r", label: "Registration", render: (v: any) => <Reg reg={v.registration} /> },
                { key: "m", label: "Vehicle", render: (v: any) => [v.year, v.make, v.model].filter(Boolean).join(" ") || "—" },
                { key: "f", label: "Owned since", render: (v: any) => date(v.owned_from) },
              ]} />
              {previousVehicles.length > 0 && <div className="card-body small muted">Previously owned: {previousVehicles.map((v: any) => v.registration).join(", ")}</div>}
            </>}
          </Card>
        </div>
        <div className="stack">
          <Card title="Account">
            <dl className="kv">
              <dt>Owed to us</dt><dd><Money pence={owed} className={owed > 0 ? "badge red" : ""} /></dd>
              <dt>Credit held</dt><dd><Money pence={creditPence} /> {creditPence > 0 && <span className="small muted">(deposit/overpayment)</span>}</dd>
            </dl>
            {owner && creditPence > 0 && <div style={{ marginTop: 12 }}><Button size="sm" onClick={() => setRefund(true)}>Refund credit</Button></div>}
          </Card>
          {mergedFrom.length > 0 && <MergedFromCard mergedFrom={mergedFrom} owner={owner} />}
          <Card title="Details">
            <dl className="kv">
              <dt>Phone</dt><dd>{c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : "—"}</dd>
              <dt>Email</dt><dd>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : "—"}</dd>
              <dt>Address</dt><dd>{[c.address, c.postcode].filter(Boolean).join(", ") || "—"}</dd>
              <dt>Reminders</dt><dd>{c.marketing_ok ? "Yes" : "No"}</dd>
              <dt>Customer since</dt><dd>{date(c.created_at.slice(0, 10))}</dd>
            </dl>
            {c.notes && <p style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{c.notes}</p>}
          </Card>
          {owner && <div className="small muted">Record ID: <span className="mono">{c.id}</span></div>}
        </div>
      </div>
      {editing && <CustomerFormModal customerId={c.id} initial={{ name: c.name, phone: c.phone ?? "", email: c.email ?? "", address: c.address ?? "", postcode: c.postcode ?? "", notes: c.notes ?? "", contactPref: c.contact_pref, marketingOk: !!c.marketing_ok }}
        onClose={() => setEditing(false)} onSaved={() => setEditing(false)} />}
      {refund && <RefundModal customerId={c.id} maxPence={creditPence} onClose={() => setRefund(false)} />}
    </>
  );
}

function MergedFromCard({ mergedFrom, owner }: { mergedFrom: any[]; owner: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const undo = useMutation({
    mutationFn: (mergeId: string) => api.post(`/customers/merges/${mergeId}/undo`),
    onSuccess: () => { ["customers", "customer", "customer-duplicates"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast("success", "Merge undone"); },
    onError: (e) => toast("error", (e as Error).message),
  });
  return (
    <Card title="Merged records">
      {mergedFrom.map((m) => <div key={m.id} className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <div><Link to={`/customers/${m.id}`}>{m.name}</Link><div className="small muted">merged {date(m.merged_at.slice(0, 10))}{m.credit_pence ? ` · credit on that record: £${(m.credit_pence / 100).toFixed(2)}` : ""}</div></div>
        {owner && <Button size="sm" variant="ghost" loading={undo.isPending} onClick={() => { if (confirm(`Undo the merge of “${m.name}”? It goes back to being a separate customer with its vehicles.`)) undo.mutate(m.merge_id); }}>Undo</Button>}
      </div>)}
    </Card>
  );
}
